import React from 'react';
import { sanitizeHtml, sanitizeStyleValue } from '../../Sanitizer.jsx';
import { useTemplateContactInfo } from '../../TemplateContactUtils.jsx';
import {
  ButtonLink,
  getActions,
  getImageUrl,
  toStyle,
} from '../shared/templateSectionUtils.jsx';
const genericResponsiveCss = `
  [data-template-id] .generic-template-grid,
  [data-template-id] .generic-template-card-grid {
    min-width: 0;
  }
  @media (max-width: 900px) {
    [data-template-id] .generic-template-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
`;

const HeroSection = ({ section }) => {
  const content = section.content || {};
  const style = section.style || {};
  const layout = section.layout || {};
  const align = layout.align || 'left';
  const imageUrl = getImageUrl(content);
  const hasImage = Boolean(imageUrl);

  return (
    <section
      style={{
        ...toStyle(style),
        minHeight: style.minHeight || 420,
        padding: style.padding || '72px 24px',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: layout.width === 'full' ? 'none' : 1180,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: hasImage ? 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))' : 'minmax(0, 1fr)',
          gap: 32,
          alignItems: 'center',
          textAlign: align
        }}
      >
        <div>
          {content.eyebrow && <p style={{ color: style.accentColor || '#3B9394', fontWeight: 700, marginBottom: 12 }}>{content.eyebrow}</p>}
          {content.title && <h1 style={{ fontSize: 44, lineHeight: 1.08, margin: '0 0 16px', color: style.color || '#1e293b' }}>{content.title}</h1>}
          {content.subtitle && <p style={{ fontSize: 18, lineHeight: 1.7, maxWidth: align === 'center' ? 760 : 620, margin: align === 'center' ? '0 auto 24px' : '0 0 24px', color: style.color || '#475569' }}>{content.subtitle}</p>}
          <ButtonLink label={content.primaryActionLabel} url={content.primaryActionUrl} accentColor={style.accentColor} />
        </div>
        {hasImage && (
          <img
            src={imageUrl}
            alt={content.imageAlt || content.title || 'Template'}
            style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 8 }}
          />
        )}
      </div>
    </section>
  );
};

const FeatureGridSection = ({ section }) => {
  const content = section.content || {};
  const items = Array.isArray(content.items) ? content.items : [];
  return (
    <section style={{ ...toStyle(section.style), padding: section.style?.padding || '56px 24px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        {content.title && <h2 style={{ margin: '0 0 24px', fontSize: 30 }}>{content.title}</h2>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {items.map((item, index) => (
            <article key={`${item.title}-${index}`} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, background: '#fff' }}>
              <h3 style={{ marginTop: 0 }}>{item.title}</h3>
              <p style={{ color: '#64748b' }}>{item.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContentSection = ({ section }) => {
  const content = section.content || {};
  return (
    <section style={{ ...toStyle(section.style), padding: section.style?.padding || '48px 24px' }}>
      <div style={{ maxWidth: 920, margin: '0 auto', textAlign: section.layout?.align || 'left' }}>
        {content.title && <h2>{content.title}</h2>}
        {content.subtitle && <p style={{ fontSize: 17, lineHeight: 1.7 }}>{content.subtitle}</p>}
      </div>
    </section>
  );
};

const ImageBannerSection = ({ section }) => {
  const content = section.content || {};
  const style = section.style || {};
  const imageUrl = getImageUrl(content);
  return (
    <section
      style={{
        ...toStyle(style),
        minHeight: style.minHeight || 420,
        padding: style.padding || '72px 24px',
        display: 'grid',
        placeItems: 'center',
        backgroundImage: imageUrl ? `linear-gradient(90deg, rgba(15,23,42,.68), rgba(15,23,42,.18)), url("${sanitizeStyleValue(imageUrl)}")` : style.backgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div style={{ maxWidth: 900, margin: '0 auto', textAlign: section.layout?.align || 'center', color: style.color || '#ffffff' }}>
        {content.eyebrow && <p style={{ fontWeight: 700, marginBottom: 12 }}>{content.eyebrow}</p>}
        {content.title && <h1 style={{ fontSize: 42, lineHeight: 1.1, margin: '0 0 16px' }}>{content.title}</h1>}
        {content.subtitle && <p style={{ fontSize: 18, lineHeight: 1.7, margin: '0 0 24px' }}>{content.subtitle}</p>}
        <ButtonLink label={content.primaryActionLabel} url={content.primaryActionUrl} accentColor={style.accentColor} />
      </div>
    </section>
  );
};

const FAQSection = ({ section }) => {
  const content = section.content || {};
  const items = Array.isArray(content.items) ? content.items : [];
  return (
    <section style={{ ...toStyle(section.style), padding: section.style?.padding || '56px 24px' }}>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        {content.title && <h2 style={{ margin: '0 0 24px', fontSize: 30 }}>{content.title}</h2>}
        <div style={{ display: 'grid', gap: 12 }}>
          {items.map((item, index) => (
            <details key={`${item.title}-${index}`} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 18, background: '#fff' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{item.title}</summary>
              <p style={{ color: '#64748b', lineHeight: 1.7 }}>{item.description}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = ({ section }) => {
  const content = section.content || {};
  const items = Array.isArray(content.items) ? content.items : [];
  return (
    <section style={{ ...toStyle(section.style), padding: section.style?.padding || '56px 24px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        {content.title && <h2 style={{ margin: '0 0 24px', fontSize: 30 }}>{content.title}</h2>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {items.map((item, index) => (
            <figure key={`${item.title}-${index}`} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 22, background: '#fff', margin: 0 }}>
              <blockquote style={{ color: '#334155', lineHeight: 1.7, margin: 0 }}>{item.description}</blockquote>
              <figcaption style={{ marginTop: 14, fontWeight: 700 }}>{item.title}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactBlockSection = ({ section }) => {
  const content = section.content || {};
  const contactInfo = useTemplateContactInfo(content);
  const resolved = { ...content, email: contactInfo.emailValue, phone: contactInfo.phoneValue, address: contactInfo.addressValue };
  return (
    <section style={{ ...toStyle(section.style), padding: section.style?.padding || '56px 24px' }}>
      <div style={{ maxWidth: 920, margin: '0 auto', textAlign: section.layout?.align || 'left' }}>
        {content.title && <h2 style={{ fontSize: 30 }}>{content.title}</h2>}
        {content.subtitle && <p style={{ fontSize: 17, lineHeight: 1.7 }}>{content.subtitle}</p>}
        <div style={{ display: 'grid', gap: 8, marginTop: 20, color: '#475569' }}>
          {resolved.email && <a href={`mailto:${resolved.email}`}>{resolved.email}</a>}
          {resolved.phone && <a href={`tel:${resolved.phone.replace(/\s/g, '')}`}>{resolved.phone}</a>}
          {resolved.address && <span style={{ whiteSpace: 'pre-line' }}>{resolved.address}</span>}
        </div>
      </div>
    </section>
  );
};

const CustomHtmlSection = ({ section }) => {
  const html = sanitizeHtml(section.customHtml || '');
  return (
    <section style={{ ...toStyle(section.style), padding: section.style?.padding || '32px 24px' }}>
      {html ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <div style={{ maxWidth: 920, margin: '0 auto', border: '1px dashed #cbd5e1', borderRadius: 8, padding: 24, color: '#64748b', textAlign: 'center' }}>
          Custom HTML is empty or was removed by sanitizer.
        </div>
      )}
    </section>
  );
};

const getGenericActions = (section) => {
  const content = section?.content || {};
  const configuredActions = getActions(section).filter((action) => action?.label);
  if (configuredActions.length) return configuredActions;

  return [
    content.primaryActionLabel ? { label: content.primaryActionLabel, url: content.primaryActionUrl, variant: 'primary' } : null,
    content.secondaryActionLabel ? { label: content.secondaryActionLabel, url: content.secondaryActionUrl, variant: 'outline' } : null
  ].filter(Boolean);
};

const getGenericBlocks = (section) => {
  const content = section?.content || {};
  const keys = ['blocks', 'items', 'features', 'cards', 'services', 'benefits', 'stats', 'roles', 'plans', 'scheduleOptions', 'categories'];
  const seen = new Set();

  return keys.flatMap((key) => {
    const value = content[key];
    return Array.isArray(value) ? value.filter((item) => {
      const id = `${key}-${item?.title || item?.label || item?.name || item?.value || JSON.stringify(item)}`;
      if (item?.visible === false || seen.has(id)) return false;
      seen.add(id);
      return true;
    }) : [];
  });
};

const genericImageFieldPairs = [
  ['heroImageUrl', 'heroImageContent'],
  ['coverImageUrl', 'coverImageContent'],
  ['bannerImageUrl', 'bannerImageContent'],
  ['mediaUrl', 'mediaContent'],
  ['imageUrl', 'imageContent'],
  ['signupImageUrl', 'signupImageContent'],
  ['illustrationUrl', 'illustrationContent']
];

const genericContentImage = (content = {}) => (
  genericImageFieldPairs
    .map(([urlKey, assetKey]) => (
      content[assetKey]?.displayLink
      || content[assetKey]?.storedLink
      || content[urlKey]
      || ''
    ))
    .find(Boolean)
  || content.imageURL
  || ''
);

function GenericTemplateSection({ section, theme = {}, fallbackTitle = '' }) {
  const content = section.content || {};
  const align = section.layout?.align || section.style?.textAlign || 'left';
  const isCentered = align === 'center';
  const title = content.title || content.heading || content.headline || content.name || fallbackTitle;
  const subtitle = content.subtitle || content.description || content.body || content.text || '';
  const imageUrl = genericContentImage(content);
  const blocks = getGenericBlocks(section);
  const actions = getGenericActions(section);
  const html = sanitizeHtml(content.html || content.richText || content.bodyHtml || '');

  if (!title && !subtitle && !imageUrl && !blocks.length && !actions.length && !html && !section.customHtml) {
    return null;
  }

  return (
    <section
      style={{
        backgroundColor: theme.background || '#ffffff',
        color: theme.color || '#1e293b',
        padding: section.style?.padding || '64px 24px',
        ...toStyle(section.style)
      }}
    >
      <div
        className={imageUrl ? 'generic-template-grid' : ''}
        style={{
          maxWidth: section.layout?.width === 'full' ? 'none' : section.style?.maxWidth || theme.pageWidth || 1180,
          margin: '0 auto',
          display: imageUrl ? 'grid' : 'block',
          gridTemplateColumns: 'minmax(0, .92fr) minmax(280px, .8fr)',
          gap: 36,
          alignItems: 'center',
          textAlign: align
        }}
      >
        <div>
          {content.eyebrow && <p style={{ margin: '0 0 10px', color: theme.primary || '#3B9394', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', fontSize: 12 }}>{content.eyebrow}</p>}
          {title && <h2 style={{ margin: '0 0 14px', fontSize: 'clamp(28px, 4vw, 44px)', lineHeight: 1.12, color: section.style?.headingColor || theme.color || '#1e293b' }}>{title}</h2>}
          {subtitle && <p style={{ margin: isCentered ? '0 auto 24px' : '0 0 24px', maxWidth: 720, color: theme.mutedColor || '#64748b', fontSize: 17, lineHeight: 1.7 }}>{subtitle}</p>}
          {html && <div style={{ color: theme.color || '#1e293b', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: html }} />}
          {actions.length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: isCentered ? 'center' : 'flex-start', marginTop: 24 }}>
              {actions.map((action, index) => {
                const outline = action.variant === 'outline' || action.style === 'outline';
                return (
                  <a
                    key={`${action.label}-${index}`}
                    href={action.url || action.route || '#'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 42,
                      padding: '0 18px',
                      borderRadius: theme.buttonRadius || 8,
                      border: `1px solid ${outline ? theme.borderColor || '#e5e7eb' : 'transparent'}`,
                      background: outline ? theme.surface || '#ffffff' : theme.primary || '#3B9394',
                      color: outline ? theme.color || '#1e293b' : '#ffffff',
                      textDecoration: 'none',
                      fontWeight: 800
                    }}
                  >
                    {action.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
        {imageUrl && (
          <img
            src={imageUrl}
            alt={content.imageAlt || title || 'Template section'}
            style={{ width: '100%', aspectRatio: section.layout?.imageRatio || '4 / 3', objectFit: 'cover', borderRadius: theme.cardRadius || 12, boxShadow: theme.cardShadow || '0 10px 30px rgba(15,23,42,.10)' }}
          />
        )}
      </div>
      {blocks.length > 0 && (
        <div
          className="generic-template-card-grid"
          style={{
            maxWidth: section.style?.maxWidth || theme.pageWidth || 1180,
            margin: '34px auto 0',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 18
          }}
        >
          {blocks.map((item, index) => {
            const itemImage = getImageUrl(item);
            return (
              <article key={`${item.title || item.label || item.name || index}-${index}`} style={{ border: `1px solid ${theme.borderColor || '#e5e7eb'}`, borderRadius: theme.cardRadius || 12, background: theme.surface || '#ffffff', boxShadow: theme.cardShadow || 'none', overflow: 'hidden', minWidth: 0 }}>
                {itemImage && <img src={itemImage} alt={item.imageAlt || item.title || 'Section item'} style={{ width: '100%', height: 160, objectFit: 'cover' }} />}
                <div style={{ padding: 18 }}>
                  {(item.icon || item.value || item.price) && <strong style={{ display: 'inline-flex', color: theme.primary || '#3B9394', marginBottom: 8 }}>{item.icon || item.value || item.price}</strong>}
                  {(item.title || item.label || item.name) && <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{item.title || item.label || item.name}</h3>}
                  {(item.description || item.subtitle || item.body) && <p style={{ margin: 0, color: theme.mutedColor || '#64748b', lineHeight: 1.65 }}>{item.description || item.subtitle || item.body}</p>}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
export {
  ContactBlockSection,
  ContentSection,
  CustomHtmlSection,
  FAQSection,
  FeatureGridSection,
  GenericTemplateSection,
  HeroSection,
  ImageBannerSection,
  TestimonialsSection,
  genericResponsiveCss,
};
